"use client"

import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
  Chip,
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import { ThemeProvider } from "@mui/material/styles"
import {
  extractErrorMessage,
  formatFecha,
  formatHora,
  getEmpleadoNombreCompleto,
  rrhhTheme,
} from "./rrhhUi.js"

const buildEmptyAsignacion = (defaultTurno = null) => ({
  asignacion: {
    idEntidad: null,
    idTurno: defaultTurno?.id || null,
    fechaVigenciaDesde: "",
    fechaVigenciaHasta: "",
    observaciones: "",
  },
  cerrarAsignacionAnterior: true,
})

const ModalRrhhAsignacionTurno = ({
  open,
  empleadosOptions = [],
  turnosOptions = [],
  defaultTurno = null,
  onClose,
  onSave,
  onLoadAsignaciones,
}) => {
  const [formData, setFormData] = useState(buildEmptyAsignacion(defaultTurno))
  const [saving, setSaving] = useState(false)
  const [loadingAsignaciones, setLoadingAsignaciones] = useState(false)
  const [asignacionesActuales, setAsignacionesActuales] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setFormData(buildEmptyAsignacion(defaultTurno))
      setSaving(false)
      setError(null)
      setAsignacionesActuales([])
    }
  }, [open, defaultTurno])

  useEffect(() => {
    const fetchAsignaciones = async () => {
      if (!open || !formData.asignacion.idEntidad) {
        setAsignacionesActuales([])
        return
      }

      try {
        setLoadingAsignaciones(true)
        const response = await onLoadAsignaciones(formData.asignacion.idEntidad)
        setAsignacionesActuales(response?.data || [])
      } catch (loadError) {
        console.error("Error cargando asignaciones RRHH:", loadError)
      } finally {
        setLoadingAsignaciones(false)
      }
    }

    fetchAsignaciones()
  }, [open, formData.asignacion.idEntidad, onLoadAsignaciones])

  const selectedEmpleado = useMemo(
    () => empleadosOptions.find((option) => option.idEntidad === formData.asignacion.idEntidad) || null,
    [empleadosOptions, formData.asignacion.idEntidad]
  )

  const selectedTurno = useMemo(
    () => turnosOptions.find((option) => option.id === formData.asignacion.idTurno) || null,
    [turnosOptions, formData.asignacion.idTurno]
  )

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      asignacion: {
        ...prev.asignacion,
        [field]: event.target.value,
      },
    }))
  }

  const handleSubmit = async () => {
    if (!formData.asignacion.idEntidad || !formData.asignacion.idTurno || !formData.asignacion.fechaVigenciaDesde) {
      setError("Empleado, turno y fecha de vigencia desde son requeridos.")
      return
    }

    try {
      setSaving(true)
      setError(null)
      await onSave(formData)
    } catch (submitError) {
      setError(extractErrorMessage(submitError, "No se pudo asignar el turno."))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ThemeProvider theme={rrhhTheme}>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Asignar turno
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vincula una plantilla horaria a un empleado con vigencia controlada.
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={3}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={empleadosOptions}
                  value={selectedEmpleado}
                  onChange={(_, option) => {
                    setFormData((prev) => ({
                      ...prev,
                      asignacion: {
                        ...prev.asignacion,
                        idEntidad: option?.idEntidad || null,
                      },
                    }))
                  }}
                  getOptionLabel={(option) => `${getEmpleadoNombreCompleto(option)}${option.legajo ? ` • ${option.legajo}` : ""}`}
                  renderInput={(params) => <TextField {...params} label="Empleado" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={turnosOptions}
                  value={selectedTurno}
                  onChange={(_, option) => {
                    setFormData((prev) => ({
                      ...prev,
                      asignacion: {
                        ...prev.asignacion,
                        idTurno: option?.id || null,
                      },
                    }))
                  }}
                  getOptionLabel={(option) =>
                    `${option.descripcion}${option.horaEntrada && option.horaSalida ? ` • ${formatHora(option.horaEntrada)}-${formatHora(option.horaSalida)}` : ""}`
                  }
                  renderInput={(params) => <TextField {...params} label="Turno" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Vigencia desde"
                  type="date"
                  value={formData.asignacion.fechaVigenciaDesde}
                  onChange={handleChange("fechaVigenciaDesde")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Vigencia hasta"
                  type="date"
                  value={formData.asignacion.fechaVigenciaHasta}
                  onChange={handleChange("fechaVigenciaHasta")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Observaciones"
                  value={formData.asignacion.observaciones}
                  onChange={handleChange("observaciones")}
                  fullWidth
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.cerrarAsignacionAnterior}
                  onChange={(event) => {
                    setFormData((prev) => ({
                      ...prev,
                      cerrarAsignacionAnterior: event.target.checked,
                    }))
                  }}
                />
              }
              label="Cerrar automáticamente la asignación anterior abierta"
            />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Asignaciones actuales del empleado
              </Typography>

              {loadingAsignaciones ? (
                <Typography variant="body2" color="text.secondary">
                  Cargando asignaciones...
                </Typography>
              ) : asignacionesActuales.length > 0 ? (
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} flexWrap="wrap" useFlexGap>
                  {asignacionesActuales.slice(0, 6).map((asignacion) => (
                    <Chip
                      key={asignacion.id}
                      label={`${asignacion.turno?.descripcion || "Turno"} • ${formatFecha(asignacion.fechaVigenciaDesde)}${asignacion.fechaVigenciaHasta ? ` a ${formatFecha(asignacion.fechaVigenciaHasta)}` : " en curso"}`}
                    />
                  ))}
                </Stack>
              ) : (
                <Alert severity="info">El empleado no tiene asignaciones de turno registradas.</Alert>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={saving}>
            {saving ? "Guardando..." : "Asignar turno"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}

export default ModalRrhhAsignacionTurno
